import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import classNames from 'classnames'
import { connect } from 'react-redux'

import Loader from '../../components/Loader'

import { ROUTE } from '../../config'
import Icon from '../../components/Icon'
import { generatePublicationLink, isWarningLightsPublication } from '../../utils/publication'
import { getRootTopicForWarningLightsPublication } from '../../utils/topics'

import '../../styles/TopicsList.scss'
import './InfinitiTopicsList.scss'
import DownloadIcon from '../../svg/download'

export const getLabelTitle = (topic) => {
  if (topic.video) {
    return 'Video'
  }

  if (topic.bodyPdf) {
    return 'PDF'
  }

  if (topic.bodyHtml || topic.type === 'topic') {
    return 'Topic'
  }

  return ''
}

const TopicsList = ({
  topics,
  publications,
  emptyText = 'No topics found',
  baseRoute,
  className,
  onClick,
  allTopics,
  contentIndex,
  isRoot,
}) => {
  const topicElements = useMemo(() => {
    const publicationsList = publications ? publications.toJS() : []
    let topicsList = topics

    let topicElements = []

    // group publications by publicationType + title (except UG and DEFAULT => unique per resourceKey)
    const groupedPubs = {}
    publicationsList.forEach(publication => {
      const publicationType = publication.publicationType
      const title = publication.title
      const resourceKey = publication.resourceKey
      if (publicationType === 'UG' || publicationType === 'DEFAULT' || !publicationType) {
        const key = `${publicationType || 'DEFAULT'}-${resourceKey}`
        groupedPubs[key] = [publication]
      } else {
        const key = `${publicationType}-${title}`
        if (!groupedPubs[key]) groupedPubs[key] = []
        groupedPubs[key].push(publication)
      }
    })

    const renderedGroups = new Set()
    let publicationTypes = {}
    let index = 0

    topicsList.forEach((topic, key) => {
      const isFolder = topic.get('type') === 'folder'
      const isPublication = topic.get('type') === 'publication'
      const resourceKey = topic.get('resourceKey')

      let publicationType = ''
      if (isPublication) {
        const pubInfo = contentIndex[topic.get('id')]
        const isWarningLightsPub = isWarningLightsPublication(pubInfo)
        publicationType = pubInfo && pubInfo.publicationType
        const title = topic.get('title')

        let groupKey = ''
        if (publicationType === 'UG' || publicationType === 'DEFAULT' || !publicationType) {
          groupKey = `${publicationType || 'DEFAULT'}-${resourceKey}`
        } else {
          groupKey = `${publicationType}-${title}`
        }

        if (renderedGroups.has(groupKey)) return true
        renderedGroups.add(groupKey)

        if (isWarningLightsPub) {
          const warnLightsRootTopic = getRootTopicForWarningLightsPublication(allTopics, publications)
          const warnLink = `${baseRoute}${ROUTE.TOPIC}/${warnLightsRootTopic.get('resourceKey')}`

          topicElements.push(
            <li key={key + 1}>
              <div className='item-inner with-label'>
                <div className='item-links'>
                  <Link
                    to={warnLink}
                    className='nav-button view-button'
                    onClick={() => { onClick && onClick(topic) }}
                  >
                    View
                  </Link>
                </div>
                <div className='item-title'>
                  {title}
                </div>
              </div>
            </li>
          )
        } else {
          const pubsForGroup = groupedPubs[groupKey] || []
          const labelTitle = getLabelTitle(
            isRoot
              ? publicationsList.find((p) => p.resourceKey === topic.get('resourceKey'))
              : topic.toJS(),
          )
          const shouldShowLabelIcon = !isFolder && labelTitle

          topicElements.push(
            <li key={key + 1}>
              <div className={classNames('item-inner', shouldShowLabelIcon && 'with-label')}>
                <div className={`item-links ${!pubsForGroup?.[0]?.bodyPdf ? 'button-order' : ''}`}>
                  {pubsForGroup.map(pub => {
                    const isPdfPublication = !!pub.bodyPdf
                    const link = `${baseRoute}${generatePublicationLink({ isPdfPublication, isFolder, resourceKey: pub.resourceKey })}`
                    const label = isPdfPublication ? 'PDF' : 'View'
                    return (
                      <Link
                        key={pub.resourceKey}
                        to={link}
                        className={`nav-button ${!isPdfPublication ? 'view-button' : ''}`}
                        onClick={() => { onClick && onClick(topic) }}
                      >
                        {label === 'PDF' && <DownloadIcon className='download-icon' fill='#191919' />}{label}
                      </Link>
                    )
                  })}
                </div>
                <div className='item-title'>
                  {title}
                </div>
              </div>
            </li>
          )
        }
      } else {
        const isPdfPublication = publicationsList.some(
          publication => publication.resourceKey === resourceKey && !!publication.bodyPdf,
        )
        const link = `${baseRoute}${generatePublicationLink({ isPdfPublication, isFolder, resourceKey })}`
        const labelTitle = getLabelTitle(
          isRoot ? publicationsList.find((p) => p.resourceKey === topic.get('resourceKey')) : topic.toJS(),
        )
        const shouldShowLabelIcon = !isFolder && labelTitle

        topicElements.push(
          <li key={key + 1}>
            <Link
              className={classNames('item-inner', shouldShowLabelIcon && 'with-label')}
              to={link}
              onClick={() => { onClick && onClick(topic) }}
            >
              <div className='item-title'>
                {topic.get('title')} <Icon type='arrow-left' />
              </div>
            </Link>
          </li>
        )
      }

      publicationTypes[publicationType] = Array.isArray(publicationTypes[publicationType]) ? [...publicationTypes[publicationType], index] : [index]
      ++index
      return true
    })

    let orderedTopicElements = []
    if (publicationTypes['QRG']) {
      publicationTypes['QRG'].forEach(index => orderedTopicElements.push(topicElements[index]))
      delete publicationTypes['QRG']
    }
    if (publicationTypes['OM']) {
      publicationTypes['OM'].forEach(index => orderedTopicElements.push(topicElements[index]))
      delete publicationTypes['OM']
    }
    if (publicationTypes['NM']) {
      publicationTypes['NM'].forEach(index => orderedTopicElements.push(topicElements[index]))
      delete publicationTypes['NM']
    }
    if (publicationTypes['UG']) {
      publicationTypes['UG'].forEach(index => orderedTopicElements.push(topicElements[index]))
      delete publicationTypes['UG']
    }
    const otherPubs = Object.keys(publicationTypes)
    if (otherPubs?.length) {
      otherPubs.forEach(pub => {
        publicationTypes[pub].forEach(index => orderedTopicElements.push(topicElements[index]))
      })
    }

    return orderedTopicElements
  }, [topics, publications])

  if (topics === null || topics.size === 0) {
    return (
      <Loader type='status' className='inverse'>
        {emptyText}
      </Loader>
    )
  }

  return <ul className={classNames('TopicsList InfinitiTopicsList', className)}>{topicElements}</ul>
}

TopicsList.propTypes = {
  topics: PropTypes.oneOfType([ImmutablePropTypes.list, ImmutablePropTypes.map]),
  publications: ImmutablePropTypes.list,
  onBookmarkClick: PropTypes.func,
  referrer: PropTypes.oneOf([ROUTE.BROWSE, ROUTE.FAVORITES, ROUTE.TOPPICKS, ROUTE.EXPLORE, ROUTE.SEARCH]),
  emptyText: PropTypes.string,
  baseRoute: PropTypes.string.isRequired,
  className: PropTypes.string,
  onClick: PropTypes.func,
  allTopics: ImmutablePropTypes.list,
  contentIndex: PropTypes.shape({
    publicationType: PropTypes.string,
  }),
  isRoot: PropTypes.bool,
}

const mapStateToProps = (state) => ({
  publications: state.contents.publications,
  allTopics: state.contents.topics,
  contentIndex: state.contents.contentIndex,
})

export default connect(mapStateToProps)(TopicsList)
